import {
  Button,
  Input,
  message,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableColumnsType } from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { makeGetCall, makePostCall } from '../../utils/helper';
import ErrorBoundary from '../../components/ErrorBoundary';

const { Text } = Typography;

type PushStatus = 'PENDING' | 'COMPLETED' | 'PARTIAL' | 'FAILED';

interface PushLog {
  npl_id: number;
  npl_study_uid: string;
  npl_node_id: number;
  npl_node_aet: string;
  npl_status: PushStatus;
  npl_pushed_by: string;
  npl_pushed_at: string;
  npl_completed_at: string | null;
  npl_error: string | null;
  npl_instance_count: number;
  npl_exported_count: number | null;
}

// Poll fast while a transfer is in flight, slow to a heartbeat when idle.
const POLL_FAST_MS = 5000;
const POLL_SLOW_MS = 30000;

const STATUS_OPTIONS: { value: PushStatus; label: string }[] = [
  { value: 'PENDING', label: 'Sending…' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'PARTIAL', label: 'Partial' },
  { value: 'FAILED', label: 'Failed' },
];

const formatDateTime = (iso: string | null): string => {
  if (!iso) {
    return '—';
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleString();
};

const StatusBadge: React.FC<{ record: PushLog }> = ({ record }) => {
  const { npl_status, npl_exported_count, npl_instance_count } = record;
  switch (npl_status) {
    case 'PENDING':
      return (
        <Tag icon={<SyncOutlined spin />} color="processing">
          Sending…
        </Tag>
      );
    case 'COMPLETED':
      return (
        <Tag icon={<CheckCircleOutlined />} color="success">
          {`${npl_exported_count ?? npl_instance_count} images`}
        </Tag>
      );
    case 'PARTIAL':
      return (
        <Tag icon={<WarningOutlined />} color="warning">
          {`${npl_exported_count ?? 0} of ${npl_instance_count}`}
        </Tag>
      );
    case 'FAILED':
      return (
        <Tag icon={<CloseCircleOutlined />} color="error">
          Failed
        </Tag>
      );
    default:
      return <Tag>{npl_status}</Tag>;
  }
};

const ExportTasks: React.FC = () => {
  const location = useLocation();

  // Pre-filter by study_uid when arriving from a "View status" link.
  const initialStudyUid = useMemo(() => {
    return new URLSearchParams(location.search).get('study_uid') || '';
  }, [location.search]);

  const [logs, setLogs] = useState<PushLog[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<PushStatus | null>(null);
  const [studyFilter, setStudyFilter] = useState<string>(initialStudyUid);
  const [retryingIds, setRetryingIds] = useState<Set<number>>(new Set());

  const isMountedRef = useRef<boolean>(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const fetchLogs = useCallback(
    async (opts: { silent?: boolean } = {}): Promise<void> => {
      if (!opts.silent) {
        setLoading(true);
      }
      try {
        const params = new URLSearchParams();
        const trimmedStudy = studyFilter.trim();
        if (trimmedStudy) {
          params.append('study_uid', trimmedStudy);
        }
        if (statusFilter) {
          params.append('status', statusFilter);
        }
        params.append('limit', '200');

        const res: any = await makeGetCall(`/push-logs?${params.toString()}`);

        if (res.data?.success !== false && Array.isArray(res.data?.data)) {
          if (isMountedRef.current) {
            setLogs(res.data.data as PushLog[]);
          }
        } else {
          throw new Error(res.data?.message || 'Failed to load export tasks');
        }
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || error?.message || 'Failed to load export tasks';
        console.error('Error fetching export tasks:', error);
        // Only surface a toast on the user-initiated load, not on silent polls.
        if (!opts.silent) {
          message.error(errorMessage);
        }
      } finally {
        if (!opts.silent && isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [studyFilter, statusFilter]
  );

  // Initial load + reload whenever filters change.
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Self-perpetuating poll: reschedule after every data change. Fast while any
  // row is PENDING, slow heartbeat otherwise.
  useEffect(() => {
    const hasPending = logs.some(l => l.npl_status === 'PENDING');
    const delay = hasPending ? POLL_FAST_MS : POLL_SLOW_MS;
    timerRef.current = setTimeout(() => {
      fetchLogs({ silent: true });
    }, delay);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [logs, fetchLogs]);

  const handleRetry = useCallback(
    async (record: PushLog): Promise<void> => {
      setRetryingIds(prev => new Set(prev).add(record.npl_id));
      try {
        const res: any = await makePostCall(`/push-logs/${record.npl_id}/retry`);
        if (res.data?.success) {
          message.success(res.data?.message || 'Re-push started');
          await fetchLogs({ silent: true });
        } else {
          throw new Error(res.data?.message || 'Failed to re-push');
        }
      } catch (error: any) {
        // 409 => still in progress; other statuses carry actionable messages.
        const errorMessage =
          error?.response?.data?.message || error?.message || 'Failed to re-push';
        console.error('Error re-pushing export task:', error);
        message.error(errorMessage);
      } finally {
        setRetryingIds(prev => {
          const next = new Set(prev);
          next.delete(record.npl_id);
          return next;
        });
      }
    },
    [fetchLogs]
  );

  const hasPending = useMemo(() => logs.some(l => l.npl_status === 'PENDING'), [logs]);

  const columns = useMemo<TableColumnsType<PushLog>>(
    () => [
      {
        title: 'Study',
        dataIndex: 'npl_study_uid',
        ellipsis: true,
        render: (val: string) => (
          <Tooltip title={val}>
            <Text copyable={{ text: val }} style={{ fontFamily: 'monospace', fontSize: 12 }}>
              {val}
            </Text>
          </Tooltip>
        ),
      },
      {
        title: 'Destination',
        dataIndex: 'npl_node_aet',
        width: 140,
        render: (val: string) => <Text strong>{val || '—'}</Text>,
      },
      {
        title: 'Status',
        key: 'status',
        width: 150,
        render: (_v, record) => <StatusBadge record={record} />,
      },
      {
        title: 'Images',
        key: 'images',
        width: 120,
        align: 'center',
        render: (_v, record) => {
          const exported = record.npl_exported_count;
          const target = record.npl_instance_count;
          if (exported == null) {
            return <Text type="secondary">{`— / ${target}`}</Text>;
          }
          return (
            <Text type={exported < target ? 'warning' : undefined}>
              {`${exported} / ${target}`}
            </Text>
          );
        },
      },
      {
        title: 'Pushed by',
        dataIndex: 'npl_pushed_by',
        width: 130,
        render: (val: string) => val || '—',
      },
      {
        title: 'Pushed at',
        dataIndex: 'npl_pushed_at',
        width: 180,
        render: (val: string) => formatDateTime(val),
      },
      {
        title: 'Completed at',
        dataIndex: 'npl_completed_at',
        width: 180,
        render: (val: string | null) => formatDateTime(val),
      },
      {
        title: 'Error',
        dataIndex: 'npl_error',
        ellipsis: true,
        render: (val: string | null) =>
          val ? (
            <Tooltip title={val}>
              <Text type="danger">{val}</Text>
            </Tooltip>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        align: 'center',
        render: (_v, record) => {
          const isTerminalRetryable =
            record.npl_status === 'PARTIAL' || record.npl_status === 'FAILED';
          if (!isTerminalRetryable) {
            return <Text type="secondary">—</Text>;
          }
          return (
            <Button
              size="small"
              icon={<RedoOutlined />}
              loading={retryingIds.has(record.npl_id)}
              onClick={() => handleRetry(record)}
            >
              Re-push
            </Button>
          );
        },
      },
    ],
    [retryingIds, handleRetry]
  );

  return (
    <ErrorBoundary>
      <div style={{ padding: 16 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <Space align="center">
            <Typography.Title level={4} style={{ margin: 0 }}>
              Export Tasks
            </Typography.Title>
            {hasPending && (
              <Tag icon={<SyncOutlined spin />} color="processing">
                Live
              </Tag>
            )}
          </Space>

          <Space wrap>
            <Input
              placeholder="Filter by Study UID"
              value={studyFilter}
              onChange={e => setStudyFilter(e.target.value)}
              onPressEnter={() => fetchLogs()}
              prefix={<SearchOutlined />}
              allowClear
              style={{ width: 280 }}
            />
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={value => setStatusFilter(value ?? null)}
              options={STATUS_OPTIONS}
              allowClear
              style={{ width: 150 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => fetchLogs()}>
              Refresh
            </Button>
          </Space>
        </div>

        <Spin spinning={loading} tip="Loading export tasks...">
          <Table<PushLog>
            columns={columns}
            dataSource={logs}
            rowKey="npl_id"
            size="small"
            scroll={{ x: 'max-content' }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100', '200'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tasks`,
            }}
            locale={{
              emptyText:
                studyFilter || statusFilter
                  ? 'No export tasks match the current filters'
                  : 'No export tasks yet',
            }}
          />
        </Spin>
      </div>
    </ErrorBoundary>
  );
};

export default ExportTasks;
